import { Box, MenuItem, Stack, TextField } from '@mui/material';
import React from 'react';

import { RemoteAutocomplete } from '../../components/RemoteAutocomplete';

// Target picker for the `sender` match type (CP-8268): who *sent* the chat
// message that triggered the notification. Either one specific user, or any
// member of an access group (RBAC group) — e.g. "Guides", which is the case this
// was built for.
//
// Value shape (owned by the caller):
//   { sender_user: {id,email,name} | null, sender_access_group: {id,name} | null }
// Exactly one of the two is set; picking on one side clears the other, because
// that is what the backend accepts. Objects rather than bare ids so an existing
// rule's target is labelled without an extra lookup.
//
// Both lookups are scoped to the caller's own tenant server-side, so a foreign
// user or group is not offered here and would be rejected on save anyway. The
// house RemoteAutocomplete provides the debounce, caching and stale-response
// handling — don't hand-roll the fetching again.

const BY_USER = 'user';
const BY_GROUP = 'group';

export const EMPTY_SENDER = { sender_user: null, sender_access_group: null };

const userLabel = (u) => {
    if (!u) return '';
    return u.name ? `${u.name} (${u.email})` : (u.email || `#${u.id}`);
};

const SenderField = ({ value, onChange, resource = 'blacklist_rule' }) => {
    const { sender_user: senderUser = null, sender_access_group: senderGroup = null } = value || {};

    const [by, setBy] = React.useState(senderUser ? BY_USER : BY_GROUP);

    // Keep the toggle in step when an existing rule finishes loading.
    React.useEffect(() => {
        if (senderUser) setBy(BY_USER);
        else if (senderGroup) setBy(BY_GROUP);
    }, [senderUser, senderGroup]);

    return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField select size="small" label="Sender is" value={by} sx={{ minWidth: 170 }}
                onChange={(e) => {
                    setBy(e.target.value);
                    onChange({ ...EMPTY_SENDER });
                }}>
                <MenuItem value={BY_GROUP}>In an access group</MenuItem>
                <MenuItem value={BY_USER}>A specific user</MenuItem>
            </TextField>
            <Box sx={{ flex: 1 }}>
                {by === BY_GROUP ? (
                    <RemoteAutocomplete
                        size="small"
                        fullWidth
                        resource={resource}
                        urlSuffix="sender_groups/"
                        label="Access group"
                        value={senderGroup}
                        onChange={(_, v) => onChange({ sender_user: null, sender_access_group: v || null })}
                        textFieldProps={{
                            helperText: 'Blocks when every sender of the message(s) is in this group.'
                                + ' The Guests group matches any guest sender — guests belong to it'
                                + ' implicitly.',
                        }} />
                ) : (
                    <RemoteAutocomplete
                        size="small"
                        fullWidth
                        resource={resource}
                        urlSuffix="sender_users/"
                        label="User"
                        value={senderUser}
                        getOptionLabel={userLabel}
                        filterOptions={(options) => options}
                        onChange={(_, v) => onChange({ sender_user: v || null, sender_access_group: null })}
                        textFieldProps={{
                            helperText: 'Blocks when this user sent the message',
                        }} />
                )}
            </Box>
        </Stack>
    );
};

export default SenderField;
